import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import api from '@/services/api';
import { canSetRemarkDeadline, sharePermissionLabel } from '@/components/review/sharePermission';
import type { GroupSharedSession, ReviewSession } from '@/types';

interface Props {
  open: boolean;
  groupId: string;
  groupName: string;
  onClose: () => void;
  onPublished: () => void;
}

export default function PublishToGroupDialog({ open, groupId, groupName, onClose, onPublished }: Props) {
  const [sessions, setSessions] = useState<ReviewSession[]>([]);
  const [groupSessions, setGroupSessions] = useState<GroupSharedSession[]>([]);
  const [sessionId, setSessionId] = useState('');
  const [permission, setPermission] = useState<'participant' | 'viewer'>('participant');
  const [dueAt, setDueAt] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !groupId) return;
    setError(null);
    setSessionId('');
    setMessage('');
    setDueAt('');
    setPermission('participant');
    Promise.all([
      api.get<{ sessions: ReviewSession[] }>('/review-sessions', { params: { filter: 'mine' } }),
      api.get<{ sessions: GroupSharedSession[] }>(`/groups/${groupId}/sessions`),
    ])
      .then(([mineRes, groupRes]) => {
        setSessions(mineRes.data.sessions ?? []);
        setGroupSessions(groupRes.data.sessions ?? []);
      })
      .catch(() => {
        setSessions([]);
        setGroupSessions([]);
        setError('無法載入場次列表');
      });
  }, [open, groupId]);

  const publishedSessionIds = useMemo(
    () => new Set(groupSessions.map((s) => s.session_id)),
    [groupSessions]
  );

  const availableSessions = useMemo(
    () => sessions.filter((s) => !publishedSessionIds.has(s.id)),
    [sessions, publishedSessionIds]
  );

  const showDeadline = canSetRemarkDeadline(permission);

  const handlePublish = async () => {
    if (!sessionId || publishedSessionIds.has(sessionId)) return;
    setSaving(true);
    setError(null);
    try {
      await api.post(`/shares/session/${sessionId}`, {
        share_type: 'group',
        target_group_id: groupId,
        permission,
        message: message || undefined,
        due_at: showDeadline && dueAt ? dueAt : undefined,
      });
      onPublished();
      onClose();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '發佈失敗';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>發佈影片至 {groupName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {availableSessions.length === 0 ? (
            <Alert severity="info">
              {sessions.length === 0
                ? '您尚無可發佈的分析場次'
                : '所有場次皆已發佈至本群組，無需重複發佈'}
            </Alert>
          ) : (
            <TextField
              select
              label="選擇我的分析場次"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              fullWidth
            >
              <MenuItem value="">—</MenuItem>
              {availableSessions.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.title}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            select
            label="分享權限"
            value={permission}
            onChange={(e) => {
              const next = e.target.value as 'participant' | 'viewer';
              setPermission(next);
              if (!canSetRemarkDeadline(next)) setDueAt('');
            }}
            fullWidth
            disabled={availableSessions.length === 0}
          >
            <MenuItem value="participant">{sharePermissionLabel('participant')}</MenuItem>
            <MenuItem value="viewer">{sharePermissionLabel('viewer')}</MenuItem>
          </TextField>

          {showDeadline && (
            <TextField
              label="備註截止時間"
              type="datetime-local"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              disabled={availableSessions.length === 0}
            />
          )}

          <TextField
            label="訊息"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            multiline
            rows={2}
            disabled={availableSessions.length === 0}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handlePublish}
          disabled={!sessionId || saving || publishedSessionIds.has(sessionId)}
        >
          發佈
        </Button>
      </DialogActions>
    </Dialog>
  );
}
